import { WatchfaceDeviceType } from '@/hooks';
import { ElectronApi } from '@/services/electron.api';

export const AODThemeValidRules: {
  description: string;
  deviceTypes: WatchfaceDeviceType[];
  rule: {
    // 非黑像素占比校验,不存在代表不进行该项校验
    nonBlackRatio?: number;
    // 像素颜色限制
    'max(R,G)/2>=B'?: boolean;
    // RGB不存在代表不进行该项校验
    RGB?: {
      [key in 'movable' | 'partiallyMovable' | 'notMovable']: {
        valid: (R: number, G: number, B: number) => boolean;
        desc: string;
        tooltip?: string;
      };
    };
  };
}[] = [
  {
    description: '常规表盘',
    deviceTypes:
      ElectronApi.isStaging() || import.meta.env.DEV
        ? [
            'M67',
            'M66',
            'N65',
            'N66',
            'N67',
            'O62',
            'O65',
            'O62M',
            'O65M',
            'O66',
            'Q66',
            'P67'
          ]
        : ElectronApi.isNormal()
        ? [
            'M67',
            'M66',
            'N65',
            'N66',
            'N67',
            'O62',
            'O65',
            'O62M',
            'O65M',
            'O66',
            'Q66',
            'P67'
          ]
        : [],
    rule: {
      nonBlackRatio: 0.15,
      'max(R,G)/2>=B': true,
      RGB: {
        movable: {
          valid: (R, G, B) => R >= 64 && G >= 64 && B >= 64,
          desc: '可移动元素: 红色&绿色&蓝色色值应在>=64的范围内',
          tooltip: '绑定了时间/双时区数据源的指针图片（如时针指针、分针指针、秒针指针、双时区时针/分针指针）'
        },
        partiallyMovable: {
          valid: (R, G, B) =>
            R >= 64 && R < 200 && G >= 64 && G < 200 && B >= 64 && B < 200,
          desc: '部分可移动元素: 红色&绿色&蓝色色值应在>=64且<200的范围内',
          tooltip: '绑定了时间/双时区数据源的数字图片（如数字时间）、文本（如时间文本）、进度条（如时间进度条）'
        },
        notMovable: {
          valid: (R, G, B) =>
            R >= 64 && R < 80 && G >= 64 && G < 80 && B >= 64 && B < 80,
          desc: '不可移动元素: 红色&绿色&蓝色色值应在>=64且<80的范围内',
          tooltip: '未绑定时间数据源的所有元素，如背景图片、图标、装饰图、步数/心率/天气等数据的图片、文本、数字图片、进度条等'
        }
      }
    }
  },
  {
    description: '多彩表盘',
    deviceTypes: ['O61', 'O63', 'P65', 'P62', 'N62', 'N62S', 'Q63'],
    rule: {
      nonBlackRatio: 0.5,
      RGB: {
        movable: {
          valid: (R, G, B) => R >= 32 && G >= 32 && B >= 32,
          desc: '可移动元素: 红色&绿色&蓝色色值应在>=32的范围内',
          tooltip: '绑定了时间/双时区数据源的指针图片（如时针指针、分针指针、秒针指针、双时区时针/分针指针）'
        },
        partiallyMovable: {
          valid: (R, G, B) =>
            R >= 32 && R < 200 && G >= 32 && G < 200 && B >= 32 && B < 200,
          desc: '部分可移动元素: 红色&绿色&蓝色色值应在>=32且<200的范围内',
          tooltip: '绑定了时间/双时区数据源的数字图片（如数字时间）、文本（如时间文本）、进度条（如时间进度条）'
        },
        notMovable: {
          valid: (R, G, B) =>
            R >= 32 && R < 120 && G >= 32 && G < 120 && B >= 32 && B < 120,
          desc: '不可移动元素: 红色&绿色&蓝色色值应在>=32且<120的范围内',
          tooltip: '未绑定时间数据源的所有元素，如背景图片、图标、装饰图、步数/心率/天气等数据的图片、文本、数字图片、进度条等'
        }
      }
    }
  },
  {
    description: '全彩表盘',
    deviceTypes: ['Q61'],
    rule: {
      RGB: {
        movable: {
          valid: (R, G, B) => true,
          desc: '可移动元素: 红色&绿色&蓝色色值无要求',
          tooltip: '绑定了时间/双时区数据源的指针图片（如时针指针、分针指针、秒针指针、双时区时针/分针指针）'
        },
        partiallyMovable: {
          valid: (R, G, B) =>
            R < 200 && G < 200 && B < 200,
          desc: '部分可移动元素: 红色&绿色&蓝色色值应在<200的范围内',
          tooltip: '绑定了时间/双时区数据源的数字图片（如数字时间）、文本（如时间文本）、进度条（如时间进度条）'
        },
        notMovable: {
          valid: (R, G, B) =>
            R < 120 &&  G < 120 && B < 120,
          desc: '不可移动元素: 红色&绿色&蓝色色值应在<120的范围内',
          tooltip: '未绑定时间数据源的所有元素，如背景图片、图标、装饰图、步数/心率/天气等数据的图片、文本、数字图片、进度条等'
        }
      }
    }
  },
  // 原来规则：仅校验非黑像素
  {
    description: '原来规则：仅校验非黑像素',
    deviceTypes: [
      'L61Simulator',
      'L61',
      'L67',
      'N60',
      'N65',
      'M67',
      'M62',
      'M65S',
      'M66',
      'N65A',
      'N65B',
      'N65C',
      'N66',
      'N67',
      'O65',
      'O65M',
      'O66',
      'Q66',
      'P67'
    ],
    rule: {
      nonBlackRatio: 0.15
    }
  },
  // O62
  {
    description: '仅给O62使用',
    deviceTypes: ['O62'],
    rule: {
      nonBlackRatio:
        ElectronApi.isStaging() && ElectronApi.isProfessional() ? 0.5 : 0
    }
  }
];
